import { apiClient } from './apiClient';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';

export interface DecomposeResourceLookupItem {
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
}

export interface DecomposeImprovementRequest {
  improvementRef: string;
  apply?: boolean;
  name?: string;
  assignedResourceRef?: string;
  priority?: string;
  workflowType?: string;
  itemId?: string;
}

export interface DecomposeImprovementResponse {
  artifactPatch?: Record<string, unknown>;
  lintPreview?: { outcome?: string; fixes?: Array<{ message?: string }> };
  applied?: boolean;
  persistMode?: 'import' | 'workspace_disk';
  source?: 'db' | 'workspace';
  importResult?: Record<string, unknown>;
}

function normalizeAssignmentText(text: string): string {
  return text.trim().toLowerCase().replace(/[.,;:!?]+$/, '');
}

function looksLikeResourceRef(text: string): boolean {
  const trimmed = text.trim();
  return /^(co-|ai-|person:)/i.test(trimmed);
}

/** Resolve assigned_resource_ref for decompose from queue ref or Define Conditions owner text. */
export function resolveDecomposeAssignedResourceRef(options: {
  assignedResourceRef?: string;
  resourceAssignment?: string;
  queueItems?: DecomposeResourceLookupItem[];
}): string | undefined {
  const direct = options.assignedResourceRef?.trim();
  if (direct) return direct;

  const assignment = options.resourceAssignment?.trim();
  if (!assignment) return undefined;

  if (looksLikeResourceRef(assignment)) {
    return assignment.startsWith('person:') ? assignment.slice(7).trim() : assignment;
  }

  const assignmentNorm = normalizeAssignmentText(assignment);
  for (const item of options.queueItems ?? []) {
    const ref = item.assignedResourceRef?.trim();
    const label = item.assignedResourceLabel?.trim();
    if (!ref) continue;
    if (label) {
      const labelNorm = normalizeAssignmentText(label);
      const labelPrimary = normalizeAssignmentText(label.split(/[—\-|/]/)[0] ?? label);
      if (
        labelNorm === assignmentNorm ||
        labelNorm.includes(assignmentNorm) ||
        assignmentNorm.includes(labelPrimary)
      ) {
        return ref;
      }
    }
  }

  return undefined;
}

export class ControlBridgeDecomposeError extends Error {
  constructor(
    message: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeDecomposeError';
  }
}

export async function decomposeImprovement(
  workspaceSlug: string,
  request: DecomposeImprovementRequest
): Promise<DecomposeImprovementResponse> {
  const slug = workspaceSlug.trim();
  const improvementRef = request.improvementRef.trim();
  if (!slug || !improvementRef) {
    throw new ControlBridgeDecomposeError('Workspace slug and improvement ref are required');
  }

  try {
    const { data } = await apiClient.post<DecomposeImprovementResponse>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/decompose-improvement`,
      {
        improvementRef,
        apply: request.apply ?? false,
        name: request.name,
        assignedResourceRef: request.assignedResourceRef,
        priority: request.priority,
        workflowType: request.workflowType,
        itemId: request.itemId,
      }
    );
    return data ?? {};
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    const detail = axiosErr.response?.data?.detail;
    const message = httpErrorDetailMessage(
      detail,
      axiosErr.message || 'Failed to decompose improvement'
    );
    throw new ControlBridgeDecomposeError(message, axiosErr.response?.status);
  }
}

function isImprovementArtifactType(artifactTypeRef: string): boolean {
  const ref = artifactTypeRef.toLowerCase();
  return ref.includes('improvement');
}

export function canDecomposeImprovementItem(artifactTypeRef: string | undefined): boolean {
  return Boolean(artifactTypeRef && isImprovementArtifactType(artifactTypeRef));
}

function isActivityArtifactType(artifactTypeRef: string): boolean {
  const ref = artifactTypeRef.toLowerCase();
  return ref.includes('activity') && !ref.includes('improvement');
}

/** Entity detail / Drive→detail: Start for CREATED (or DRAFTED) operational activities. */
export function canStartActivityItem(
  artifactTypeRef: string | undefined,
  currentState: string | undefined
): boolean {
  if (!artifactTypeRef || !isActivityArtifactType(artifactTypeRef)) return false;
  const state = (currentState ?? '').trim().toUpperCase();
  return state === 'CREATED' || state === 'DRAFTED';
}

const VALIDATE_REFUSED_STATES = new Set([
  'VALIDATED',
  'CLOSED',
  'SUPERSEDED',
  'REJECTED',
  'CANCELLED',
]);

/**
 * Entity detail / Guidance: Validate plank improvement → VALIDATED (pickup gate).
 * Single-jump posture; mirrors Kryspace exopanel `validate`.
 */
export function canValidateImprovementItem(
  artifactTypeRef: string | undefined,
  currentState: string | undefined
): boolean {
  if (!artifactTypeRef || !isImprovementArtifactType(artifactTypeRef)) return false;
  const state = (currentState ?? '').trim().toUpperCase();
  if (!state || VALIDATE_REFUSED_STATES.has(state)) return false;
  return true;
}

/** True when orient blocked_reason is the IMPROVEMENT_NOT_VALIDATED pickup stake. */
export function isImprovementNotValidatedBlockReason(reason: string | undefined): boolean {
  return Boolean(reason && reason.toUpperCase().includes('IMPROVEMENT_NOT_VALIDATED'));
}

/** Parse parent improvement id from IMPROVEMENT_NOT_VALIDATED blocked_reason text. */
export function improvementRefFromNotValidatedReason(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  const match = reason.match(/for\s+['"]?([A-Za-z0-9_-]+)['"]?\s*\)?\s*$/i);
  const id = match?.[1]?.trim();
  return id || undefined;
}
